import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiError, uploadFileToStorage, compressImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AnalyticsChart } from "@/components/AnalyticsChart";
import { BuyerDownloads } from "@/components/BuyerDownloads";
import { AttachFilesButton } from "@/components/AttachFiles";
import { IncludedFormats } from "@/components/IncludedFormats";
import { BulkAttach } from "@/components/BulkAttach";
import { EmailSenderCard } from "@/components/EmailSenderCard";
import { StoreSettingsCard } from "@/components/StoreSettingsCard";
import { EarlyAccessStats } from "@/components/EarlyAccessStats";
import { ProblemReports } from "@/components/ProblemReports";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { GENRES } from "@/config";
import {
  Upload, Trash2, Loader2, Music, ImageIcon, ArrowLeft, Headphones,
  MousePointerClick, Eye, DollarSign, Megaphone, Pencil, Check, X, Download, Trophy, Mail, Layers,
  Star, GripVertical, CloudUpload, RefreshCw, Sparkles, TrendingUp, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-[#121212] border-[#2a2a2a] mt-1 focus-visible:ring-[#e2ff31]";

const fmtBytes = (n) => {
  if (n == null) return "";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};
const fmtDur = (s) => {
  if (!s || !isFinite(s)) return "";
  const m = Math.floor(s / 60), sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};
const readAudioDuration = (file) => new Promise((res) => {
  try {
    const a = document.createElement("audio");
    a.preload = "metadata";
    a.onloadedmetadata = () => { const d = a.duration; URL.revokeObjectURL(a.src); res(d); };
    a.onerror = () => res(null);
    a.src = URL.createObjectURL(file);
  } catch { res(null); }
});

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#121212] border border-[#2a2a2a] rounded-xl p-4 flex items-center gap-3">
      <span className="w-10 h-10 rounded-lg bg-[#e2ff31]/10 text-[#e2ff31] flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <p className="text-2xl font-heading font-bold leading-none">{value}</p>
        <p className="text-xs text-[#9ca3af] mt-1">{label}</p>
      </div>
    </div>
  );
}

function EarlyBadge({ track, onChanged }) {
  const hrs = Math.max(1, Math.ceil((new Date(track.early_access_until) - Date.now()) / 3600000));
  const release = async () => {
    try { await api.post(`/admin/tracks/${track.id}/release`); toast.success(`“${track.title}” is now public`); onChanged(); }
    catch { toast.error("Could not release"); }
  };
  return (
    <span className="flex items-center gap-1.5">
      <span data-testid={`early-badge-${track.id}`} className="text-[10px] font-bold text-[#e2ff31] border border-[#e2ff31]/40 rounded px-1.5 py-0.5 uppercase">Early access · {hrs}h</span>
      <button data-testid={`release-now-${track.id}`} onClick={release} className="text-[10px] font-mono text-[#9ca3af] hover:text-[#e2ff31] underline">go public now</button>
    </span>
  );
}

function AdminTrackItem({ track, onChanged, onFeature }) {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    title: track.title || "", producer: track.producer || "", genre: track.genre || "Hip Hop",
    bpm: track.bpm ?? "", music_key: track.music_key || "", price: track.price ?? "",
    stems_available: !!track.stems_available,
    description: track.description || "",
  });
  const setField = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const openEdit = () => {
    setF({
      title: track.title || "", producer: track.producer || "", genre: track.genre || "Hip Hop",
      bpm: track.bpm ?? "", music_key: track.music_key || "", price: track.price ?? "",
      stems_available: !!track.stems_available,
      description: track.description || "",
    });
    setOpen(true);
  };

  const save = async () => {
    const price = parseFloat(f.price);
    if (!f.title.trim()) { toast.error("Title can't be empty"); return; }
    if (isNaN(price) || price < 0) { toast.error("Enter a valid price"); return; }
    const payload = {
      title: f.title.trim(), producer: f.producer.trim(), genre: f.genre,
      bpm: f.bpm === "" ? null : parseInt(f.bpm, 10), music_key: f.music_key.trim(),
      price, description: f.description,
    };
    setSaving(true);
    try {
      await api.patch(`/admin/tracks/${track.id}`, payload);
      toast.success("Beat updated");
      setOpen(false); onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await api.delete(`/admin/tracks/${track.id}`);
      toast.success("Beat removed");
      setConfirmOpen(false);
      onChanged();
    } catch { toast.error("Could not remove"); }
    finally { setRemoving(false); }
  };

  return (
    <div data-testid={`admin-track-${track.id}`} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 bg-[#121212] border border-[#2a2a2a] rounded-xl p-3">
      <GripVertical data-testid={`drag-${track.id}`} className="w-4 h-4 text-[#4b5563] cursor-grab active:cursor-grabbing shrink-0" />
      <img src={`${API}${track.cover_url}`} alt={track.title} className="w-12 h-12 rounded-lg object-cover" />
      <div className="min-w-0 flex-1 basis-[calc(100%-6rem)] sm:basis-auto">
        <p className="font-medium truncate flex items-center gap-2">
          {track.title}
          {track.sold && <span className="text-[10px] font-bold text-[#ef4444] border border-[#ef4444]/40 rounded px-1.5 py-0.5 uppercase">Sold</span>}
        </p>
        {track.early_access_until && !track.sold && <div className="mt-1"><EarlyBadge track={track} onChanged={onChanged} /></div>}
        <div className="text-sm text-[#9ca3af] flex items-center gap-3 mt-0.5 flex-wrap">
          <span className="truncate">{track.producer}</span>
          <span className="font-mono text-[#e2ff31]">${track.price?.toFixed(2)}</span>
          <IncludedFormats track={track} className="text-[10px]" />
          <span className="flex items-center gap-1 text-xs font-mono"><Headphones className="w-3 h-3" /> {track.play_count || 0}</span>
        </div>
      </div>
      <div className="flex items-center ml-auto shrink-0">
      <button data-testid={`feature-track-${track.id}`} onClick={() => onFeature(track)}
        title={track.featured ? "Featured on homepage" : "Feature on homepage"}
        className={`p-2 rounded-lg transition-colors hover:bg-[#1e1e1e] ${track.featured ? "text-[#e2ff31]" : "text-[#9ca3af] hover:text-[#e2ff31]"}`}>
        <Star className="w-4 h-4" fill={track.featured ? "currentColor" : "none"} />
      </button>
      <AttachFilesButton track={track} onChanged={onChanged} />
      <button data-testid={`edit-track-${track.id}`} onClick={openEdit}
        className="p-2 rounded-lg text-[#9ca3af] hover:text-[#e2ff31] hover:bg-[#1e1e1e] transition-colors">
        <Pencil className="w-4 h-4" />
      </button>
      <button data-testid={`delete-track-${track.id}`} onClick={() => setConfirmOpen(true)}
        title="Remove this beat"
        className="p-2 rounded-lg text-[#9ca3af] hover:text-[#ef4444] hover:bg-[#1e1e1e] transition-colors">
        <Trash2 className="w-4 h-4" />
      </button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid={`delete-dialog-${track.id}`} className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6] max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Trash2 className="w-5 h-5 text-[#ef4444]" /> Remove beat?</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Confirm removing this beat from your catalog.</DialogDescription>
          <p className="text-sm text-[#9ca3af]">
            This removes <span className="text-[#f3f4f6] font-medium">“{track.title}”</span> from your catalog and the store. Buyers who already own it keep their download. This can't be undone here.
          </p>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-full border-[#2a2a2a] bg-transparent hover:bg-[#1e1e1e]">Keep it</Button>
            <Button data-testid={`confirm-delete-${track.id}`} onClick={remove} disabled={removing}
              className="rounded-full bg-[#ef4444] text-white hover:bg-[#dc2626] font-semibold">
              {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Remove beat"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`edit-dialog-${track.id}`} className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit beat</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-[#9ca3af] text-sm">Title</Label>
              <Input data-testid={`edit-title-${track.id}`} value={f.title} onChange={(e) => setField("title", e.target.value)} className={inputCls} />
            </div>
            <div className="col-span-2">
              <Label className="text-[#9ca3af] text-sm">Producer</Label>
              <Input data-testid={`edit-producer-${track.id}`} value={f.producer} onChange={(e) => setField("producer", e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-[#9ca3af] text-sm">Genre</Label>
              <Select value={f.genre} onValueChange={(v) => setField("genre", v)}>
                <SelectTrigger data-testid={`edit-genre-${track.id}`} className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6]">
                  {GENRES.map((g) => <SelectItem key={g} value={g} className="focus:bg-[#1e1e1e] cursor-pointer">{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#9ca3af] text-sm">Price (USD)</Label>
              <Input data-testid={`edit-price-${track.id}`} type="number" step="0.01" min="0" value={f.price} onChange={(e) => setField("price", e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-[#9ca3af] text-sm">BPM</Label>
              <Input data-testid={`edit-bpm-${track.id}`} type="number" value={f.bpm} onChange={(e) => setField("bpm", e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-[#9ca3af] text-sm">Key</Label>
              <Input data-testid={`edit-key-${track.id}`} value={f.music_key} onChange={(e) => setField("music_key", e.target.value)} className={inputCls} />
            </div>
            <div className="col-span-2 rounded-lg border border-[#2a2a2a] px-3 py-2">
              <p className="text-sm">Included with purchase: <IncludedFormats track={track} className="text-xs" /></p>
              <p className="text-xs text-[#6b7280]">Use the paperclip button to attach or replace the WAV master and stems.</p>
            </div>
            <div className="col-span-2">
              <Label className="text-[#9ca3af] text-sm">Description</Label>
              <Textarea data-testid={`edit-desc-${track.id}`} value={f.description} onChange={(e) => setField("description", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full border-[#2a2a2a] bg-transparent hover:bg-[#1e1e1e]">Cancel</Button>
            <Button data-testid={`edit-save-${track.id}`} onClick={save} disabled={saving}
              className="rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AutoImportPanel({ onImported }) {
  const [items, setItems] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(null);
  const load = () => api.get("/admin/auto-import").then((r) => setItems(r.data)).catch(() => {});
  const loadIncoming = () => api.get("/admin/incoming").then((r) => setIncoming(r.data.files || [])).catch(() => {});
  const [removingName, setRemovingName] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const startRename = (name) => { setEditingName(name); setEditValue(name); };
  const cancelRename = () => { setEditingName(null); setEditValue(""); };
  const audioCores = [...new Set(incoming.filter((f) => f.role === "audio").map((f) => f.core))];
  const pairWith = async (file, core) => {
    const ext = file.name.includes(".") ? file.name.split(".").pop() : "";
    const dest = ext ? `${core}.${ext}` : core;
    try {
      await api.post("/admin/incoming/rename", { src: file.name, dest });
      toast.success(`Paired with “${core}”`, {
        action: { label: "Import now", onClick: () => scan(true) },
      });
      loadIncoming();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not pair file");
    }
  };
  const saveRename = async (oldName) => {
    const dest = editValue.trim();
    if (!dest || dest === oldName) { cancelRename(); return; }
    setSavingRename(true);
    try {
      await api.post("/admin/incoming/rename", { src: oldName, dest });
      toast.success(`Renamed to “${dest}”`, {
        action: { label: "Import now", onClick: () => scan(true) },
      });
      cancelRename();
      loadIncoming();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not rename file");
    } finally { setSavingRename(false); }
  };
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const clearQueue = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/admin/incoming");
      setIncoming([]);
      setClearOpen(false);
      toast.success(`Cleared ${data.removed} file(s) from the queue`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not clear the queue");
      loadIncoming();
    } finally { setClearing(false); }
  };
  const removeIncoming = async (name) => {
    setRemovingName(name);
    setIncoming((prev) => prev.filter((f) => f.name !== name)); // optimistic
    try {
      await api.delete(`/admin/incoming/${encodeURIComponent(name)}`);
      toast.success(`Removed “${name}” from the queue`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove file");
      loadIncoming(); // restore on failure
    } finally { setRemovingName(null); }
  };
  useEffect(() => {
    load();
    loadIncoming();
    const t = setInterval(loadIncoming, 6000);
    return () => clearInterval(t);
  }, []);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    for (const f of files) {
      try {
        const toSend = f.type?.startsWith("image/") ? await compressImage(f) : f;
        setUploading({ name: toSend.name, pct: 0 });
        await uploadFileToStorage(toSend, "incoming", (pct) => setUploading({ name: toSend.name, pct }));
      } catch (e) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setUploading(null);
    toast.success(`${files.length} file(s) sent to beats/incoming/ — importing shortly`);
    loadIncoming();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const fmtSize = (n) => {
    if (!n) return "";
    const u = ["B", "KB", "MB", "GB"]; let i = 0; let v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
  };

  const scan = async (force = false) => {
    setScanning(true);
    try {
      const { data } = await api.post(`/admin/auto-import/scan${force ? "?force=true" : ""}`);
      if (data.error) toast.error(data.error);
      else if (data.imported) { toast.success(`Imported ${data.imported} beat(s): ${(data.titles || []).join(", ")}`); onImported?.(); }
      else toast(force ? "Nothing ready to import yet — pair a file with its audio first" : "No new files found in beats/incoming/ — you're all caught up");
      load();
      loadIncoming();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Scan failed");
    } finally { setScanning(false); }
  };

  return (
    <div data-testid="auto-import-panel" className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-lg bg-[#e2ff31]/10 text-[#e2ff31] flex items-center justify-center shrink-0"><CloudUpload className="w-5 h-5" /></span>
          <div>
            <p className="font-heading text-lg font-semibold">Auto-import from Google Cloud</p>
            <p className="text-xs text-[#9ca3af] mt-1 max-w-xl leading-relaxed">
              Drop an audio file into <code className="text-[#e2ff31]">beats/incoming/</code> — it auto-appears within ~2 min with a real waveform.
              Name it <code className="text-[#e2ff31]">Genre - Title - 140bpm - 29.99.mp3</code> for instant details, and drop same-named
              files to pair them: an image (cover), a <strong>.wav</strong> (WAV master) and a <strong>.zip</strong> (stems). BPM &amp; key are auto-detected when not in the name.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="auto-import-scan-btn" onClick={() => scan(false)} disabled={scanning}
            className="flex items-center gap-2 text-sm rounded-full border border-[#2a2a2a] text-[#f3f4f6] hover:border-[#e2ff31]/50 font-semibold px-4 py-2.5 disabled:opacity-60 transition-colors">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Scan now
          </button>
          <button data-testid="auto-import-now-btn" onClick={() => scan(true)} disabled={scanning}
            className="flex items-center gap-2 text-sm rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold px-4 py-2.5 disabled:opacity-60 transition-colors">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import now
          </button>
        </div>
      </div>

      <label data-testid="drop-zone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`mt-4 flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed cursor-pointer py-6 transition-colors ${dragOver ? "border-[#e2ff31] bg-[#e2ff31]/5" : "border-[#2a2a2a] hover:border-[#e2ff31]/50"}`}>
        <input data-testid="drop-input" type="file" multiple className="hidden"
          onChange={(e) => handleFiles(e.target.files)} accept="audio/*,image/*,.wav,.zip" />
        {uploading ? (
          <div className="w-full max-w-sm px-6 text-center">
            <p className="text-xs text-[#9ca3af] truncate">Uploading {uploading.name}…</p>
            <div className="mt-2 h-1.5 bg-[#2a2a2a] rounded-full overflow-hidden">
              <div className="h-full bg-[#e2ff31] transition-all" style={{ width: `${uploading.pct}%` }} />
            </div>
          </div>
        ) : (
          <>
            <CloudUpload className="w-6 h-6 text-[#e2ff31]" />
            <p className="text-sm text-[#f3f4f6] font-medium">Drag &amp; drop beats here</p>
            <p className="text-xs text-[#6b7280]">or click to browse — files go straight to <code className="text-[#9ca3af]">beats/incoming/</code></p>
          </>
        )}
      </label>

      <div className="mt-4 border-t border-[#2a2a2a] pt-3" data-testid="incoming-queue">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280] flex items-center gap-2">
            <span className={`w-1.5 h-1.5 rounded-full ${incoming.length ? "bg-[#e2ff31] animate-pulse" : "bg-[#3a3a3a]"}`} />
            Incoming queue ({incoming.length})
          </p>
          {incoming.length > 0 && (
            <button data-testid="clear-queue-btn" onClick={() => setClearOpen(true)}
              className="flex items-center gap-1.5 text-[11px] text-[#9ca3af] hover:text-[#ef4444] transition-colors">
              <Trash2 className="w-3.5 h-3.5" /> Clear queue
            </button>
          )}
        </div>
        {incoming.length === 0 ? (
          <p className="text-xs text-[#6b7280]">No files waiting. Drop beats into <code className="text-[#9ca3af]">beats/incoming/</code> and they'll show up here live.</p>
        ) : (
          <div className="space-y-1.5">
            {incoming.map((f) => (
              <div key={f.name} data-testid={`incoming-file-${f.name}`}
                className={`text-xs bg-[#0a0a0a] border rounded-lg px-3 py-2 ${f.importable === false ? "border-[#f59e0b]/40" : "border-[#2a2a2a]"}`}>
                <div className="flex items-center justify-between gap-3">
                  {editingName === f.name ? (
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <Input data-testid={`rename-input-${f.name}`} value={editValue}
                        onChange={(e) => setEditValue(e.target.value)} autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(f.name); if (e.key === "Escape") cancelRename(); }}
                        className="h-7 text-xs bg-[#121212] border-[#2a2a2a] focus-visible:ring-[#e2ff31]" />
                      <button data-testid={`rename-save-${f.name}`} onClick={() => saveRename(f.name)} disabled={savingRename}
                        title="Save name" className="p-1 rounded-md text-[#22c55e] hover:bg-[#1e1e1e] disabled:opacity-50">
                        {savingRename ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      </button>
                      <button data-testid={`rename-cancel-${f.name}`} onClick={cancelRename}
                        title="Cancel" className="p-1 rounded-md text-[#6b7280] hover:text-[#ef4444] hover:bg-[#1e1e1e]">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <span className="truncate text-[#f3f4f6]">{f.name}</span>
                  )}
                  {editingName !== f.name && (
                    <span className="flex items-center gap-3 shrink-0 text-[#9ca3af]">
                      <span className="font-mono">{fmtSize(f.size)}</span>
                      {f.importable === false
                        ? <span data-testid={`incoming-blocked-${f.name}`} className="text-[#f59e0b] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> can't import</span>
                        : f.ready
                          ? <span className="text-[#e2ff31] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> importing…</span>
                          : <span>waiting {f.eta}s</span>}
                      <button data-testid={`rename-incoming-${f.name}`} onClick={() => startRename(f.name)}
                        title="Rename to pair with its audio"
                        className="p-1 rounded-md text-[#6b7280] hover:text-[#e2ff31] hover:bg-[#1e1e1e] transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`remove-incoming-${f.name}`} onClick={() => removeIncoming(f.name)}
                        disabled={removingName === f.name} title="Remove from queue"
                        className="p-1 rounded-md text-[#6b7280] hover:text-[#ef4444] hover:bg-[#1e1e1e] transition-colors disabled:opacity-50">
                        {removingName === f.name ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                      </button>
                    </span>
                  )}
                </div>
                {f.hint && (
                  <p data-testid={`incoming-hint-${f.name}`} className={`mt-1 ${f.importable === false ? "text-[#f59e0b]" : "text-[#6b7280]"}`}>
                    {f.hint}
                  </p>
                )}
                {f.importable === false && editingName !== f.name && audioCores.length > 0 && (
                  <div className="mt-2 flex items-center gap-2" data-testid={`pair-with-${f.name}`}>
                    <span className="text-[11px] text-[#9ca3af] shrink-0">One-tap pair:</span>
                    <Select onValueChange={(core) => pairWith(f, core)}>
                      <SelectTrigger data-testid={`pair-select-${f.name}`}
                        className="h-7 w-auto min-w-[140px] text-xs bg-[#121212] border-[#2a2a2a] focus:ring-[#e2ff31]">
                        <SelectValue placeholder="Pair with audio…" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6]">
                        {audioCores.map((core) => (
                          <SelectItem key={core} value={core} data-testid={`pair-option-${f.name}-${core}`}
                            className="text-xs focus:bg-[#1e1e1e] cursor-pointer">{core}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent data-testid="clear-queue-dialog" className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6] max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Trash2 className="w-5 h-5 text-[#ef4444]" /> Clear the queue?</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Confirm removing all files waiting in the incoming queue.</DialogDescription>
          <p className="text-sm text-[#9ca3af]">
            This removes <span className="text-[#f3f4f6] font-medium">all {incoming.length} file(s)</span> waiting in <code className="text-[#e2ff31]">beats/incoming/</code>. Beats already imported into your catalog are not affected.
          </p>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setClearOpen(false)} className="rounded-full border-[#2a2a2a] bg-transparent hover:bg-[#1e1e1e]">Keep them</Button>
            <Button data-testid="confirm-clear-queue" onClick={clearQueue} disabled={clearing}
              className="rounded-full bg-[#ef4444] text-white hover:bg-[#dc2626] font-semibold">
              {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Clear queue"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {items.length > 0 && (
        <div className="mt-4 border-t border-[#2a2a2a] pt-3">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280] mb-2">Recently auto-imported</p>
          <div className="flex flex-wrap gap-2">
            {items.slice(0, 8).map((it) => (
              <span key={it.track_id} data-testid={`auto-import-item-${it.track_id}`}
                className="text-xs bg-[#0a0a0a] border border-[#2a2a2a] rounded-full px-3 py-1 text-[#9ca3af]">
                {it.title}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BeatsTab() {
  const [tracks, setTracks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("");
  const [form, setForm] = useState({ title: "", producer: "", genre: "Hip Hop", bpm: "", music_key: "", price: "", description: "" });
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [stems, setStems] = useState(null);
  const [wav, setWav] = useState(null);
  const [audioMeta, setAudioMeta] = useState(null);
  const [stemsMeta, setStemsMeta] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  const onAudioChange = async (e) => {
    const f = e.target.files[0]; setAudio(f);
    if (f) { const d = await readAudioDuration(f); setAudioMeta({ size: f.size, duration: d }); }
    else setAudioMeta(null);
  };
  const onStemsChange = (e) => {
    const f = e.target.files[0]; setStems(f);
    setStemsMeta(f ? { size: f.size } : null);
  };
  const onCoverChange = (e) => {
    const f = e.target.files[0]; setCover(f);
    setCoverPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return f ? URL.createObjectURL(f) : null; });
  };

  const load = () => api.get("/admin/tracks").then((r) => setTracks(r.data));
  useEffect(() => { load(); }, []);

  const [dragInfo, setDragInfo] = useState(null);
  const groupsByGenre = useMemo(() => {
    const g = {};
    for (const t of tracks) (g[t.genre || "Other"] = g[t.genre || "Other"] || []).push(t);
    return g;
  }, [tracks]);

  const handleDrop = async (genre, fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const group = tracks.filter((t) => (t.genre || "Other") === genre);
    const reordered = [...group];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    let ri = 0;
    const newTracks = tracks.map((t) => ((t.genre || "Other") === genre ? reordered[ri++] : t));
    setTracks(newTracks); // optimistic
    try {
      await api.post("/admin/tracks/reorder", { ordered_ids: reordered.map((t) => t.id) });
      toast.success("Order saved");
    } catch { toast.error("Could not save order"); load(); }
  };

  const featureTrack = async (track) => {
    try {
      await api.post(`/admin/tracks/${track.id}/feature`, { featured: !track.featured });
      toast.success(track.featured ? "Removed from homepage feature" : "Featured on homepage");
      load();
    } catch { toast.error("Could not update featured beat"); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!audio || !cover) { toast.error("Please attach both an MP3 and a cover image."); return; }
    setSubmitting(true);
    try {
      setPhase("Uploading audio…"); setProgress(0);
      const audioRef = await uploadFileToStorage(audio, "audio", setProgress);

      let stemsRef = null;
      if (stems) {
        setPhase("Uploading stems…"); setProgress(0);
        stemsRef = await uploadFileToStorage(stems, "stems", setProgress);
      }

      let wavRef = null;
      if (wav) {
        setPhase("Uploading WAV…"); setProgress(0);
        wavRef = await uploadFileToStorage(wav, "wav", setProgress);
      }

      setPhase("Uploading cover…"); setProgress(0);
      const coverToUpload = await compressImage(cover);
      const coverRef = await uploadFileToStorage(coverToUpload, "cover", setProgress);

      setPhase("Finishing up…");
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== "") fd.append(k, v); });
      fd.append("audio_path", audioRef.path);
      fd.append("audio_content_type", audioRef.content_type || audio.type || "audio/mpeg");
      fd.append("audio_filename", audioRef.filename || audio.name);
      if (stemsRef) {
        fd.append("stems_path", stemsRef.path);
        fd.append("stems_content_type", stemsRef.content_type || stems.type || "");
        fd.append("stems_filename", stemsRef.filename || stems.name);
      }
      fd.append("cover_path", coverRef.path);
      if (wavRef) { fd.append("wav_path", wavRef.path); fd.append("wav_filename", wav.name); }
      fd.append("cover_content_type", coverRef.content_type || cover.type || "image/jpeg");
      fd.append("cover_filename", coverRef.filename || cover.name);
      await api.post("/admin/tracks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Beat uploaded!");
      setForm({ title: "", producer: "", genre: "Hip Hop", bpm: "", music_key: "", price: "", description: "" });
      setAudio(null); setCover(null); setStems(null); setWav(null);
      setAudioMeta(null); setStemsMeta(null); setCoverPreview(null);
      e.target.reset(); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setSubmitting(false); setPhase(""); setProgress(0); }
  };

  return (
    <div className="space-y-8">
      <AutoImportPanel onImported={load} />
      <BulkAttach tracks={tracks} onDone={load} />
      <div className="grid lg:grid-cols-2 gap-10">
      <form onSubmit={submit} className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-6 space-y-4 h-fit" data-testid="upload-form">
        <h2 className="font-heading text-xl font-semibold">Upload a new beat</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-[#9ca3af] text-sm">Title *</Label>
            <Input data-testid="track-title-input" value={form.title} onChange={set("title")} required className={inputCls} placeholder="Midnight Drive" />
          </div>
          <div className="col-span-2">
            <Label className="text-[#9ca3af] text-sm">Producer *</Label>
            <Input data-testid="track-producer-input" value={form.producer} onChange={set("producer")} required className={inputCls} placeholder="Prod. by you" />
          </div>
          <div>
            <Label className="text-[#9ca3af] text-sm">Genre</Label>
            <Select value={form.genre} onValueChange={(v) => setForm({ ...form, genre: v })}>
              <SelectTrigger data-testid="track-genre-input" className={inputCls}>
                <SelectValue placeholder="Select genre" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6]">
                {GENRES.map((g) => (
                  <SelectItem key={g} value={g} data-testid={`genre-option-${g}`} className="focus:bg-[#1e1e1e] cursor-pointer">{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#9ca3af] text-sm">Price (USD) *</Label>
            <Input data-testid="track-price-input" type="number" step="0.01" min="0" value={form.price} onChange={set("price")} required className={inputCls} placeholder="29.99" />
          </div>
          <div>
            <Label className="text-[#9ca3af] text-sm">BPM</Label>
            <Input data-testid="track-bpm-input" type="number" value={form.bpm} onChange={set("bpm")} className={inputCls} placeholder="140" />
          </div>
          <div>
            <Label className="text-[#9ca3af] text-sm">Key</Label>
            <Input data-testid="track-key-input" value={form.music_key} onChange={set("music_key")} className={inputCls} placeholder="C# Min" />
          </div>
          <div className="col-span-2">
            <Label className="text-[#9ca3af] text-sm">Description</Label>
            <Textarea data-testid="track-desc-input" value={form.description} onChange={set("description")} className={inputCls} placeholder="Dark, moody trap beat..." />
          </div>
          <div className="col-span-2">
            <Label className="text-[#9ca3af] text-sm flex items-center gap-2"><Music className="w-4 h-4" /> MP3 file *</Label>
            <input data-testid="track-audio-input" type="file" accept="audio/*" onChange={onAudioChange} required
              className="mt-1 block w-full text-sm text-[#9ca3af] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#e2ff31] file:text-black file:font-semibold hover:file:bg-[#c8e619] cursor-pointer" />
            {audioMeta && (
              <p data-testid="audio-meta" className="mt-1.5 text-xs font-mono text-[#e2ff31] flex items-center gap-2">
                <span>{fmtBytes(audioMeta.size)}</span>
                {audioMeta.duration ? <><span className="text-[#4b5563]">·</span><span>{fmtDur(audioMeta.duration)}</span></> : null}
              </p>
            )}
          </div>
          <div className="col-span-2">
            <Label className="text-[#9ca3af] text-sm flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Cover image *</Label>
            <input data-testid="track-cover-input" type="file" accept="image/*" onChange={onCoverChange} required
              className="mt-1 block w-full text-sm text-[#9ca3af] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#e2ff31] file:text-black file:font-semibold hover:file:bg-[#c8e619] cursor-pointer" />
            {coverPreview && (
              <div className="mt-2 flex items-center gap-3">
                <img data-testid="cover-preview" src={coverPreview} alt="cover preview" className="w-16 h-16 rounded-lg object-cover border border-[#2a2a2a]" />
                <span className="text-xs font-mono text-[#e2ff31]">{fmtBytes(cover?.size)}</span>
              </div>
            )}
            <p className="mt-1 text-[11px] text-[#6b7280]">Artwork is auto-optimized before upload for faster loads — MP3/WAV are uploaded at full quality.</p>
          </div>
          <div className="col-span-2 border-t border-[#2a2a2a] pt-3 mt-1">
            <p className="text-xs text-[#6b7280] mb-2">Optional extras — every exclusive includes them at the same single price.</p>
            <Label className="text-[#9ca3af] text-sm flex items-center gap-2"><Music className="w-4 h-4" /> WAV master (optional)</Label>
            <input data-testid="track-wav-input" type="file" accept=".wav,audio/wav,audio/x-wav" onChange={(e) => setWav(e.target.files[0] || null)}
              className="mt-1 mb-3 block w-full text-sm text-[#9ca3af] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#1e1e1e] file:text-[#e2ff31] file:font-semibold hover:file:bg-[#2a2a2a] cursor-pointer" />
            {wav && <p data-testid="wav-meta" className="-mt-2 mb-3 text-xs font-mono text-[#e2ff31]">{fmtBytes(wav.size)}</p>}
            <Label className="text-[#9ca3af] text-sm flex items-center gap-2"><Layers className="w-4 h-4" /> Stems ZIP (optional)</Label>
            <input data-testid="track-stems-input" type="file" accept=".wav,.mp3,audio/*,.zip" onChange={onStemsChange}
              className="mt-1 block w-full text-sm text-[#9ca3af] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#1e1e1e] file:text-[#e2ff31] file:font-semibold hover:file:bg-[#2a2a2a] cursor-pointer" />
            {stemsMeta && (
              <p data-testid="stems-meta" className="mt-1.5 text-xs font-mono text-[#e2ff31]">{fmtBytes(stemsMeta.size)}</p>
            )}
          </div>
        </div>
        <Button data-testid="upload-submit-btn" type="submit" disabled={submitting}
          className="w-full rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold">
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {phase || "Uploading…"}</> : <><Upload className="w-4 h-4 mr-2" /> Upload beat</>}
        </Button>
        {submitting && (phase === "Uploading audio…" || phase === "Uploading stems…" || phase === "Uploading WAV…" || phase === "Uploading cover…") && (
          <div data-testid="upload-progress" className="space-y-1">
            <div className="flex justify-between text-xs text-[#9ca3af]">
              <span>{phase}</span><span className="font-mono">{progress}%</span>
            </div>
            <div className="h-2 rounded-full bg-[#0a0a0a] border border-[#2a2a2a] overflow-hidden">
              <div className="h-full bg-[#e2ff31] transition-[width] duration-200" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </form>

      <div>
        <h2 className="font-heading text-xl font-semibold mb-1">Your catalog ({tracks.length})</h2>
        <p className="text-xs text-[#6b7280] mb-4">Drag the handle to reorder how beats appear in each genre column, and tap the star to feature one beat on the homepage.</p>
        {tracks.length === 0 ? (
          <p className="text-[#9ca3af] text-sm">No beats yet. Upload your first one!</p>
        ) : (
          <div className="space-y-6">
            {Object.keys(groupsByGenre).map((genre) => (
              <div key={genre} data-testid={`admin-genre-${genre}`}>
                <h3 className="text-xs font-mono uppercase tracking-widest text-[#9ca3af] mb-2">
                  {genre} <span className="text-[#6b7280]">({groupsByGenre[genre].length})</span>
                </h3>
                <div className="space-y-3">
                  {groupsByGenre[genre].map((t, idx) => (
                    <div key={t.id}
                      draggable
                      onDragStart={() => setDragInfo({ genre, index: idx })}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragInfo && dragInfo.genre === genre) handleDrop(genre, dragInfo.index, idx);
                        setDragInfo(null);
                      }}>
                      <AdminTrackItem track={t} onChanged={load} onFeature={featureTrack} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

const RANGE_PRESETS = [
  { key: "all", label: "All time" },
  { key: "month", label: "This month" },
  { key: "30d", label: "Last 30d" },
  { key: "7d", label: "Last 7d" },
];

function computeRange(key) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (key === "all") return { start: "", end: "" };
  if (key === "month") return { start: iso(new Date(today.getFullYear(), today.getMonth(), 1)), end: iso(today) };
  if (key === "30d") { const s = new Date(today); s.setDate(s.getDate() - 29); return { start: iso(s), end: iso(today) }; }
  if (key === "7d") { const s = new Date(today); s.setDate(s.getDate() - 6); return { start: iso(s), end: iso(today) }; }
  return { start: "", end: "" };
}

function TopEarners() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });

  const buildQuery = (r) => {
    const p = new URLSearchParams();
    if (r.start) p.set("start", r.start);
    if (r.end) p.set("end", r.end);
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/stats/tracks${buildQuery(range)}`)
      .then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [range]);

  const selectPreset = (key) => {
    setPreset(key);
    setRange(computeRange(key));
  };

  const setCustom = (field) => (e) => {
    setPreset("custom");
    setRange((r) => ({ ...r, [field]: e.target.value }));
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/stats/export${buildQuery(range)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "pulse-analytics.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-6" data-testid="top-earners">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#e2ff31]" /> Top earners
        </h2>
        <button data-testid="export-csv-btn" onClick={exportCsv} disabled={exporting}
          className="flex items-center gap-2 text-sm rounded-full border border-[#2a2a2a] px-4 py-2 hover:bg-[#1e1e1e] transition-colors disabled:opacity-50">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export CSV
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="flex gap-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded-full p-1">
          {RANGE_PRESETS.map((p) => (
            <button key={p.key} data-testid={`earners-range-${p.key}`} onClick={() => selectPreset(p.key)}
              className={`text-xs font-medium rounded-full px-3 py-1 transition-colors ${preset === p.key ? "bg-[#e2ff31] text-black" : "text-[#9ca3af] hover:text-white"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-[#6b7280]">
          <input type="date" data-testid="earners-start" value={range.start} onChange={setCustom("start")}
            className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-2 py-1 text-[#9ca3af] focus:border-[#e2ff31] outline-none" />
          <span>→</span>
          <input type="date" data-testid="earners-end" value={range.end} onChange={setCustom("end")}
            className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-2 py-1 text-[#9ca3af] focus:border-[#e2ff31] outline-none" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#e2ff31]" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[#6b7280] py-10 text-center">No beats yet. Upload a beat to start tracking performance.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="border-[#2a2a2a] hover:bg-transparent">
              <TableHead className="text-[#9ca3af]">Beat</TableHead>
              <TableHead className="text-[#9ca3af] text-right">Plays</TableHead>
              <TableHead className="text-[#9ca3af] text-right">Sales</TableHead>
              <TableHead className="text-[#9ca3af] text-right">Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`earner-row-${r.id}`} className="border-[#2a2a2a] hover:bg-[#1e1e1e]">
                <TableCell className="font-medium">{r.title}</TableCell>
                <TableCell className="text-right font-mono text-[#9ca3af]">{r.plays.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono text-[#9ca3af]">{r.sales.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono text-[#e2ff31]">${r.revenue.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function MonetizationTab() {
  const [stats, setStats] = useState(null);
  const [ads, setAds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [adType, setAdType] = useState("banner");
  const [title, setTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [skipAfter, setSkipAfter] = useState(5);
  const [cpm, setCpm] = useState("");
  const [cpc, setCpc] = useState("");
  const [media, setMedia] = useState(null);

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  const loadAds = () => api.get("/admin/ads").then((r) => setAds(r.data)).catch(() => {});
  useEffect(() => { loadStats(); loadAds(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!media) { toast.error(adType === "banner" ? "Attach a banner image." : "Attach an audio ad."); return; }
    setSubmitting(true);
    const fd = new FormData();
    fd.append("ad_type", adType); fd.append("title", title); fd.append("link_url", linkUrl);
    fd.append("skip_after", skipAfter); fd.append("cpm", cpm || 0); fd.append("cpc", cpc || 0);
    fd.append("media", media);
    try {
      await api.post("/admin/ads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Ad campaign created!");
      setTitle(""); setLinkUrl(""); setCpm(""); setCpc(""); setMedia(null); e.target.reset(); loadAds(); loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create ad");
    } finally { setSubmitting(false); }
  };

  const toggle = async (id) => {
    try { await api.patch(`/admin/ads/${id}/toggle`); loadAds(); } catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/ads/${id}`); toast.success("Ad removed"); loadAds(); loadStats(); }
    catch { toast.error("Failed"); }
  };

  const [sendingSummary, setSendingSummary] = useState(false);
  const sendSummary = async () => {
    setSendingSummary(true);
    try {
      await api.post("/admin/send-summary");
      toast.success("Summary email sent! Check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send email");
    } finally { setSendingSummary(false); }
  };

  return (
    <div className="space-y-10">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Headphones} label="Total plays" value={(stats?.total_plays ?? 0).toLocaleString()} />
        <StatCard icon={Eye} label="Ad impressions" value={(stats?.total_ad_impressions ?? 0).toLocaleString()} />
        <StatCard icon={MousePointerClick} label="Ad clicks" value={(stats?.total_ad_clicks ?? 0).toLocaleString()} />
        <StatCard icon={DollarSign} label="Est. ad revenue" value={`$${(stats?.total_ad_revenue ?? 0).toLocaleString()}`} />
        <StatCard icon={DollarSign} label="Beats sold" value={(stats?.total_sales ?? 0).toLocaleString()} />
      </div>

      {/* Weekly email summary */}
      <div className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="email-summary-card">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-[#e2ff31]/10 text-[#e2ff31] flex items-center justify-center"><Mail className="w-5 h-5" /></span>
          <div>
            <p className="font-medium">Weekly email summary</p>
            <p className="text-xs text-[#9ca3af]">Auto-sent every Monday 9:00 UTC to comannbeats@gmail.com</p>
          </div>
        </div>
        <button data-testid="send-summary-btn" onClick={sendSummary} disabled={sendingSummary}
          className="flex items-center gap-2 text-sm rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold px-5 py-2.5 disabled:opacity-60 transition-colors">
          {sendingSummary ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send me a summary now
        </button>
      </div>

      {/* Trend chart */}
      <AnalyticsChart />

      {/* Top earners + CSV export */}
      <TopEarners />

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Create ad */}
        <form onSubmit={submit} className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-6 space-y-4 h-fit" data-testid="ad-form">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#e2ff31]" /> New ad campaign</h2>
          <div>
            <Label className="text-[#9ca3af] text-sm">Ad type</Label>
            <div className="flex gap-2 mt-2">
              <button type="button" data-testid="ad-type-banner" onClick={() => setAdType("banner")}
                className={`flex-1 rounded-full py-2 text-sm font-medium border transition-colors ${adType === "banner" ? "bg-[#e2ff31] text-black border-[#e2ff31]" : "border-[#2a2a2a] text-[#9ca3af] hover:bg-[#1e1e1e]"}`}>
                Banner (image)
              </button>
              <button type="button" data-testid="ad-type-preroll" onClick={() => setAdType("preroll")}
                className={`flex-1 rounded-full py-2 text-sm font-medium border transition-colors ${adType === "preroll" ? "bg-[#e2ff31] text-black border-[#e2ff31]" : "border-[#2a2a2a] text-[#9ca3af] hover:bg-[#1e1e1e]"}`}>
                Pre-roll (audio)
              </button>
            </div>
            <p className="text-xs text-[#6b7280] mt-2">
              {adType === "banner" ? "Shown above the catalog. Earns on impressions & clicks." : "Plays before a track in the player. Skippable after a few seconds."}
            </p>
          </div>
          <div>
            <Label className="text-[#9ca3af] text-sm">Advertiser / title</Label>
            <Input data-testid="ad-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="Sponsor name or message" />
          </div>
          <div>
            <Label className="text-[#9ca3af] text-sm">Click-through URL</Label>
            <Input data-testid="ad-link-input" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} className={inputCls} placeholder="https://sponsor.com" />
          </div>
          {adType === "preroll" && (
            <div>
              <Label className="text-[#9ca3af] text-sm">Skippable after (seconds)</Label>
              <Input data-testid="ad-skip-input" type="number" min="0" max="30" value={skipAfter} onChange={(e) => setSkipAfter(e.target.value)} className={inputCls} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[#9ca3af] text-sm">CPM ($ / 1,000 views)</Label>
              <Input data-testid="ad-cpm-input" type="number" step="0.01" min="0" value={cpm} onChange={(e) => setCpm(e.target.value)} className={inputCls} placeholder="2.50" />
            </div>
            <div>
              <Label className="text-[#9ca3af] text-sm">CPC ($ / click)</Label>
              <Input data-testid="ad-cpc-input" type="number" step="0.01" min="0" value={cpc} onChange={(e) => setCpc(e.target.value)} className={inputCls} placeholder="0.20" />
            </div>
          </div>
          <p className="text-xs text-[#6b7280]">Set what the sponsor pays. Earnings = views/1,000 × CPM + clicks × CPC, calculated automatically.</p>
          <div>
            <Label className="text-[#9ca3af] text-sm flex items-center gap-2">
              {adType === "banner" ? <ImageIcon className="w-4 h-4" /> : <Music className="w-4 h-4" />}
              {adType === "banner" ? "Banner image *" : "Audio ad (MP3) *"}
            </Label>
            <input data-testid="ad-media-input" type="file" accept={adType === "banner" ? "image/*" : "audio/*"} onChange={(e) => setMedia(e.target.files[0])} required
              className="mt-1 block w-full text-sm text-[#9ca3af] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#e2ff31] file:text-black file:font-semibold hover:file:bg-[#c8e619] cursor-pointer" />
          </div>
          <Button data-testid="ad-submit-btn" type="submit" disabled={submitting}
            className="w-full rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Launch ad"}
          </Button>
        </form>

        {/* Ad list */}
        <div>
          <h2 className="font-heading text-xl font-semibold mb-4">Campaigns ({ads.length})</h2>
          {ads.length === 0 ? (
            <p className="text-[#9ca3af] text-sm">No ad campaigns yet. Create one to start earning.</p>
          ) : (
            <div className="space-y-3">
              {ads.map((a) => (
                <div key={a.id} data-testid={`admin-ad-${a.id}`} className="bg-[#121212] border border-[#2a2a2a] rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-black bg-[#e2ff31] rounded-full px-2 py-0.5 font-bold">
                      {a.ad_type}
                    </span>
                    <p className="font-medium truncate flex-1">{a.title || "(untitled)"}</p>
                    <Switch data-testid={`ad-toggle-${a.id}`} checked={a.active} onCheckedChange={() => toggle(a.id)} />
                    <button data-testid={`ad-delete-${a.id}`} onClick={() => remove(a.id)}
                      className="p-2 rounded-lg text-[#9ca3af] hover:text-[#ef4444] hover:bg-[#1e1e1e] transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs font-mono text-[#6b7280]">
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {a.impressions} views</span>
                    <span className="flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> {a.clicks} clicks</span>
                    <span className="flex items-center gap-1 text-[#e2ff31]" data-testid={`ad-earnings-${a.id}`}><DollarSign className="w-3 h-3" /> {(a.earnings ?? 0).toFixed(2)}</span>
                    <span className={a.active ? "text-[#22c55e]" : "text-[#6b7280]"}>{a.active ? "● Live" : "○ Paused"}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RevenueTab() {
  const [data, setData] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(null);
  useEffect(() => { api.get("/admin/analytics/revenue").then((r) => setData(r.data)).catch(() => {}); }, []);

  const pollProgress = () => {
    const iv = setInterval(async () => {
      try {
        const { data: p } = await api.get("/admin/analyze-library/progress");
        setProgress(p);
        if (!p.running) {
          clearInterval(iv);
          setAnalyzing(false);
          toast.success(`Analysis complete — ${p.updated} beat(s) updated.`);
          api.get("/admin/analytics/revenue").then((r) => setData(r.data)).catch(() => {});
        }
      } catch { clearInterval(iv); setAnalyzing(false); }
    }, 1500);
  };

  const analyze = async () => {
    setAnalyzing(true);
    setProgress(null);
    try {
      const { data: r } = await api.post("/admin/analyze-library");
      if (!r.queued) {
        toast.info(r.message || "Everything is already analyzed.");
        setAnalyzing(false);
        return;
      }
      toast.success(r.message || `Analyzing ${r.queued} beat(s)…`);
      setProgress({ running: true, total: r.queued, done: 0, updated: 0 });
      pollProgress();
    } catch { toast.error("Could not start analysis"); setAnalyzing(false); }
  };
  const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const tip = { contentStyle: { background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 12, color: "#f3f4f6" }, cursor: { fill: "rgba(226,255,49,0.06)" }, formatter: (v) => money(v) };

  return (
    <div className="space-y-8" data-testid="revenue-tab">
      <div className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-lg bg-[#e2ff31]/10 text-[#e2ff31] flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5" /></span>
          <div>
            <p className="font-heading text-lg font-semibold">Analyze library</p>
            <p className="text-xs text-[#9ca3af] mt-1 max-w-lg">Fill in tempo, musical key, and the real waveform for any older beats that were imported without them.</p>
          </div>
        </div>
        <button data-testid="analyze-library-btn" onClick={analyze} disabled={analyzing}
          className="flex items-center gap-2 text-sm rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold px-5 py-2.5 disabled:opacity-60 transition-colors">
          {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Analyze library
        </button>
        {progress && progress.total > 0 && (
          <div data-testid="analyze-progress" className="w-full mt-2">
            <div className="flex items-center justify-between text-xs text-[#9ca3af] mb-1.5 font-mono">
              <span>{progress.running ? "Analyzing beats…" : "Done"}</span>
              <span data-testid="analyze-progress-count">{progress.done} / {progress.total}</span>
            </div>
            <div className="h-2 rounded-full bg-[#1e1e1e] overflow-hidden">
              <div
                data-testid="analyze-progress-bar"
                className="h-full bg-[#e2ff31] transition-all duration-500 rounded-full"
                style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-6">
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#e2ff31]" /> Revenue</h3>
          <span data-testid="revenue-total" className="text-2xl font-bold text-[#e2ff31]">{money(data?.total)}</span>
        </div>
        <p className="text-xs text-[#6b7280] mb-5">{data?.count || 0} sale(s) all-time</p>

        <div className="grid md:grid-cols-2 gap-8">
          <div data-testid="revenue-by-genre">
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280] mb-3">By genre</p>
            {data?.by_genre?.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.by_genre} margin={{ top: 4, right: 8, left: -12, bottom: 4 }}>
                  <XAxis dataKey="genre" tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={{ stroke: "#2a2a2a" }} tickLine={false} />
                  <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip {...tip} />
                  <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                    {data.by_genre.map((_, i) => <Cell key={i} fill="#e2ff31" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-[#6b7280] py-12 text-center">No sales yet.</p>}
          </div>
          <div data-testid="revenue-by-month">
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280] mb-3">By month</p>
            {data?.by_month?.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.by_month} margin={{ top: 4, right: 8, left: -12, bottom: 4 }}>
                  <XAxis dataKey="month" tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={{ stroke: "#2a2a2a" }} tickLine={false} />
                  <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip {...tip} />
                  <Bar dataKey="revenue" radius={[6, 6, 0, 0]} fill="#e2ff31" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-[#6b7280] py-12 text-center">No sales yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openReports, setOpenReports] = useState(0);
  const onReportCounts = useCallback((c) => setOpenReports(c.open || 0), []);
  useEffect(() => {
    if (user?.role === "admin") api.get("/admin/reports", { params: { status: "open" } }).then((r) => setOpenReports(r.data.counts.open || 0)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user === false || (user && user.role !== "admin")) navigate("/");
  }, [user, navigate]);

  return (
    <div className="min-h-screen grain pb-32">
      <Navbar />
      <div className="px-6 md:px-12 py-10 relative z-10 max-w-6xl mx-auto">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to catalog
        </button>
        <div className="flex items-center gap-3 mb-8">
          <Upload className="w-7 h-7 text-[#e2ff31]" />
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">Studio Dashboard</h1>
        </div>

        <Tabs defaultValue="beats">
          <TabsList className="bg-[#121212] border border-[#2a2a2a] mb-8 h-auto flex-wrap justify-start">
            <TabsTrigger value="beats" data-testid="tab-beats" className="data-[state=active]:bg-[#e2ff31] data-[state=active]:text-black">Beats</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="data-[state=active]:bg-[#e2ff31] data-[state=active]:text-black">Analytics</TabsTrigger>
            <TabsTrigger value="monetization" data-testid="tab-monetization" className="data-[state=active]:bg-[#e2ff31] data-[state=active]:text-black">Monetization</TabsTrigger>
            <TabsTrigger value="sales" data-testid="tab-sales" className="data-[state=active]:bg-[#e2ff31] data-[state=active]:text-black">Sales</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports" className="data-[state=active]:bg-[#e2ff31] data-[state=active]:text-black gap-1.5">
              Reports{openReports > 0 && <span data-testid="tab-reports-count" className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#f59e0b] text-black text-[10px] font-bold flex items-center justify-center">{openReports}</span>}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="beats"><BeatsTab /></TabsContent>
          <TabsContent value="analytics"><RevenueTab /></TabsContent>
          <TabsContent value="monetization"><MonetizationTab /></TabsContent>
          <TabsContent value="reports"><ProblemReports onCounts={onReportCounts} /></TabsContent>
          <TabsContent value="sales"><EmailSenderCard /><StoreSettingsCard /><EarlyAccessStats /><BuyerDownloads /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
